import { useEffect, useRef, useState } from 'react'
import { useTheme } from '../../../theme'
import RinaContextCard from '../../../components/RinaContextCard'
import PageHub from '../../../components/PageHub'
import { ROUTES } from '../../../routes'
import useDossierExposant, {
  enregistrerDossier, relireDossier, useMajDossierExposant,
} from '../../../hooks/useDossierExposant'
import { classeChamp } from '../champs'
import EtatDossierCarte from './EtatDossierCarte'
import CartePiece from './CartePiece'
import FeuilleDepot from './FeuilleDepot'
import FeuilleDeclaration from './FeuilleDeclaration'
import RechercheEntreprise from './RechercheEntreprise'
import {
  ACTIVITES, ORANGE, ROUGE, ROUGE_TEXTE, STATUTS, TEAL, VERT, pluriel, siretBienForme, teinte,
} from './dossierUi'

/** Message de RINA selon le niveau (prototype v7.5, bulle de la sous-page). */
function messageRina(etat) {
  if (!etat) return 'Vos pièces administratives pour exposer au marché.'
  if (etat.niveau === 'INCOMPLET') {
    return `Il vous manque ${pluriel(etat.manquants.length, 'élément')}. Une photo prise avec votre téléphone suffit — la mairie en a besoin pour autoriser le marché.`
  }
  if (etat.niveau === 'VERIFICATION') {
    return "Merci ! L'équipe vérifie vos pièces. Vous pouvez continuer à vous inscrire aux évènements pendant ce temps."
  }
  return "Votre dossier est complet. Je vous préviendrai avant l'échéance de votre attestation d'assurance."
}

const TitreSection = ({ children, couleur = TEAL }) => (
  <p className="text-[11px] uppercase font-bold mt-[22px] mb-2.5 mx-0.5" style={{ letterSpacing: 2, color: couleur }}>{children}</p>
)

/**
 * Page « Dossier exposant » (S73, prototype v7.5 `DossierPage`) — une Route
 * (/account/dossier). Portee par l'ORGANISATION (D1), jamais bloquante (D2).
 * Chaque choix est enregistre aussitot ; l'etat affiche est celui que l'API
 * renvoie (les regles vivent au serveur).
 */
export default function PageDossierExposant() {
  const { isWeb3 } = useTheme()
  const etat = useDossierExposant()
  const majCache = useMajDossierExposant()
  const [siret, setSiret] = useState('')
  const [erreur, setErreur] = useState(null)
  const [depot, setDepot] = useState(null) // piece en cours de depot
  const [aide, setAide] = useState(false)
  const file = useRef(Promise.resolve()) // enregistrements dans l'ordre des gestes
  const dernier = useRef(0) // seul le DERNIER enregistrement repeint l'etat

  useEffect(() => { if (etat) setSiret(etat.siret || '') }, [etat?.siret])

  // Enregistre un changement (statut, SIRET, activites) — sequentiel pour que
  // deux cases cochees vite ne se croisent pas.
  const enregistrer = (data) => {
    setErreur(null)
    const n = ++dernier.current
    file.current = file.current
      .then(() => enregistrerDossier(data))
      .then((e) => { if (n === dernier.current) majCache(e) })
      .catch((e) => {
        setErreur(e.message || 'Enregistrement impossible. Réessayez.')
        // Le retour visuel immediat etait faux : on reaffiche l'etat du serveur.
        return relireDossier().then(majCache).catch(() => {})
      })
  }

  const choisirStatut = (k) => {
    if (etat.statut === k) return
    majCache({ ...etat, statut: k }) // retour visuel immediat, l'API confirme
    enregistrer({ statut: k })
  }

  const basculerActivite = (k) => {
    const activites = etat.activites.includes(k) ? etat.activites.filter((x) => x !== k) : [...etat.activites, k]
    majCache({ ...etat, activites })
    enregistrer({ activites })
  }

  const enregistrerSiret = () => {
    if ((siret || '').replace(/\s/g, '') === (etat.siret || '').replace(/\s/g, '')) return
    enregistrer({ siret })
  }

  return (
    <PageHub retour={{ label: 'Compte', vers: ROUTES.compte.base }}>
      <div className="animate-fade-up">
        <RinaContextCard title="Dossier exposant" message={messageRina(etat)} />

        {etat === undefined && (
          <div className="glass-dark rounded-[28px] h-[150px] animate-pulse" />
        )}
        {etat === null && (
          <div className="glass-surface rounded-[20px] text-[13px] leading-relaxed text-ink/70" style={{ padding: '16px 18px' }}>
            Aucune organisation n'est liée à votre compte. Le dossier exposant se remplit au nom de l'organisation qui tient le stand.
          </div>
        )}

        {etat && (
          <>
            <EtatDossierCarte etat={etat} />
            {erreur && (
              <p role="alert" className="text-[12.5px] font-semibold leading-relaxed mt-3 rounded-xl" style={{ color: ROUGE_TEXTE, background: 'rgba(217,83,79,0.08)', padding: '10px 12px' }}>
                {erreur}
              </p>
            )}

            {/* ===== 1. STATUT ===== */}
            <TitreSection>1 · Votre statut</TitreSection>
            <div className="flex flex-col gap-[9px]" role="radiogroup" aria-label="Votre statut">
              {STATUTS.map((s) => {
                const on = etat.statut === s.k
                return (
                  <button key={s.k} role="radio" aria-checked={on} onClick={() => choisirStatut(s.k)}
                    className="rounded-[18px] flex items-center gap-3 text-left w-full"
                    style={{
                      padding: '13px 14px',
                      border: on ? `1.5px solid ${TEAL}` : '1px solid rgba(7,62,61,0.1)',
                      background: on ? teinte(TEAL, 0.08) : 'rgba(255,255,255,0.5)',
                    }}>
                    <span className="text-[22px]">{s.e}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold" style={{ color: VERT }}>{s.l}</p>
                      <p className="text-[11.5px]" style={{ color: 'rgba(7,62,61,0.55)' }}>{s.s}</p>
                    </div>
                    <span className="w-[22px] h-[22px] rounded-full flex-shrink-0 box-border"
                      style={{ border: on ? `6px solid ${TEAL}` : '2px solid rgba(7,62,61,0.25)' }} />
                  </button>
                )
              })}
            </div>
            {(etat.statut === 'pro' || etat.statut === 'asso') && (
              <div className="glass-surface rounded-[18px] mt-2.5" style={{ padding: '14px 15px' }}>
                <label htmlFor="dossier-siret" className="block text-[11px] font-bold mb-1.5" style={{ color: 'rgba(7,62,61,0.5)' }}>
                  {etat.statut === 'pro' ? 'SIRET (14 chiffres) ou SIREN (9 chiffres) *' : 'SIRET (facultatif)'}
                </label>
                <input id="dossier-siret" value={siret} inputMode="numeric" placeholder="123 456 789 00012"
                  onChange={(e) => setSiret(e.target.value)} onBlur={enregistrerSiret}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur() }}
                  className={classeChamp(isWeb3)} />
                {siret && !siretBienForme(siret) && (
                  <p className="text-[11.5px] mt-1.5" style={{ color: ROUGE }}>Un SIRET compte 14 chiffres, un SIREN 9.</p>
                )}
                {/* S74 : retrouver son SIRET par le nom (registre public) */}
                <RechercheEntreprise onChoisir={(n) => { setSiret(n); enregistrer({ siret: n }) }} />
              </div>
            )}
            {etat.statut === 'encours' ? (
              <div className="glass-surface rounded-2xl mt-2.5" style={{ padding: '13px 15px', background: teinte(TEAL, 0.08) }}>
                <p className="text-[12.5px] leading-relaxed" style={{ color: 'rgba(7,62,61,0.72)' }}>
                  Vous pouvez exposer pendant que votre démarche aboutit. Envoyez l'accusé de dépôt dès que vous l'avez, puis votre justificatif définitif.
                </p>
                <button onClick={() => setAide(true)} className="mt-2 text-[12.5px] font-extrabold" style={{ color: ORANGE }}>Comment se déclarer ? ›</button>
              </div>
            ) : (
              <button onClick={() => setAide(true)} className="mt-2.5 mx-0.5 text-[12.5px] font-bold" style={{ color: ORANGE }}>
                Pas encore déclaré ? Voir comment faire ›
              </button>
            )}

            {/* ===== 2. ACTIVITES (choix multiple) ===== */}
            <TitreSection>2 · Vos activités — plusieurs choix possibles</TitreSection>
            <div className="glass-surface rounded-[20px] overflow-hidden">
              {ACTIVITES.map((a, i) => {
                const on = etat.activites.includes(a.k)
                return (
                  <button key={a.k} role="checkbox" aria-checked={on} onClick={() => basculerActivite(a.k)}
                    className="w-full flex items-center gap-3 text-left"
                    style={{
                      padding: '13px 15px',
                      background: on ? teinte(TEAL, 0.07) : 'transparent',
                      borderTop: i ? '1px solid rgba(7,62,61,0.07)' : 'none',
                    }}>
                    <span className="text-[21px]">{a.e}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold" style={{ color: VERT }}>{a.l}</p>
                      <p className="text-[11.5px]" style={{ color: 'rgba(7,62,61,0.55)' }}>{a.s}</p>
                    </div>
                    <span className="w-6 h-6 rounded-[7px] flex-shrink-0 flex items-center justify-center text-white text-sm font-extrabold"
                      style={{ background: on ? TEAL : 'transparent', border: on ? 'none' : '2px solid rgba(7,62,61,0.25)' }}>
                      {on ? '✓' : ''}
                    </span>
                  </button>
                )
              })}
            </div>
            {(etat.activites.includes('RESTAURATION') || etat.activites.includes('TRANSFO')) && (
              <p className="text-[12px] leading-normal mt-2.5 mx-0.5" style={{ color: 'rgba(7,62,61,0.62)' }}>
                {etat.activites.includes('RESTAURATION')
                  ? '🧑🏾‍🍳 La restauration exige une attestation de formation en hygiène alimentaire (HACCP) — ajoutée à vos pièces ci-dessous.'
                  : "🧑🏾‍🍳 Pour la transformation, l'attestation d'hygiène alimentaire (HACCP) n'est pas obligatoire, mais elle rassure la mairie — ajoutée en facultatif."}
              </p>
            )}

            {/* ===== 3. PIECES ===== */}
            <TitreSection couleur={ORANGE}>3 · Vos pièces justificatives</TitreSection>
            {!etat.statut && (
              <p className="text-[12px] mx-0.5 mb-2.5" style={{ color: 'rgba(7,62,61,0.55)' }}>
                Choisissez votre statut pour voir tous les justificatifs demandés.
              </p>
            )}
            <div className="flex flex-col gap-2.5">
              {etat.pieces.map((p) => <CartePiece key={p.code} p={p} onDeposer={() => setDepot(p)} />)}
            </div>

            <div className="glass-surface rounded-2xl mt-4 flex gap-2.5 items-start" style={{ padding: '13px 15px', background: teinte(TEAL, 0.08) }}>
              <span className="text-[15px]">🔒</span>
              <p className="text-[12px] leading-relaxed" style={{ color: 'rgba(7,62,61,0.68)' }}>
                Vos pièces ne sont <strong>jamais publiques</strong>. Elles sont vérifiées par l'équipe Manrina et peuvent être transmises à la mairie ou au gestionnaire du lieu, <strong>uniquement pour l'autorisation du marché</strong>.
              </p>
            </div>
          </>
        )}
      </div>

      {depot && (
        <FeuilleDepot piece={depot} onClose={() => setDepot(null)} onEnvoye={(e) => { majCache(e); setDepot(null) }} />
      )}
      {aide && (
        <FeuilleDeclaration
          enCours={etat?.statut === 'encours'}
          onClose={() => setAide(false)}
          onDemarcheLancee={() => { setAide(false); choisirStatut('encours') }}
        />
      )}
    </PageHub>
  )
}
